/* eslint-disable max-len */
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslate } from "react-admin";
import { muiColors } from "themes/colors";
import { CustomButton } from "./CustomButton";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { IEditedArtist } from "./OrderEdit";

interface IArtistChoice {
  id: any;
  name: string;
}

interface IOrderEditItemProps {
  item: any;
  setRemovedServices: Dispatch<SetStateAction<string[]>>;
  setRemovedAddons: Dispatch<SetStateAction<string[]>>;
  setRemovedItems: Dispatch<SetStateAction<string[]>>;
  artistChoices: IArtistChoice[] | undefined;
  editedArtists: IEditedArtist[];
  setEditedArtists: Dispatch<SetStateAction<IEditedArtist[]>>;
  editedAddOnArtists: IEditedArtist[];
  setEditedAddOnArtists: Dispatch<SetStateAction<IEditedArtist[]>>;
  everyRemoved: string[];
  everyEdited: IEditedArtist[];
}

const OrderEditItem = ({
  item,
  setRemovedServices,
  artistChoices,
  editedArtists,
  setEditedArtists,
  editedAddOnArtists,
  setEditedAddOnArtists,
  setRemovedAddons,
  setRemovedItems,
  everyRemoved,
  everyEdited
}: IOrderEditItemProps) => {
  const translate = useTranslate();
  const [selectedArtist, setSelectedArtist] = useState<any>({
    id: item?.artist?.id,
    name: `${item?.artist?.user?.first_name} ${item?.artist?.user?.last_name}`
  });
  return (
    <Box
      className="flex gap-1 justify-between min-h-[80px] items-center py-2 px-4 border-b border-mono-100 relative flex-wrap sm:flex-nowrap"
      sx={{
        "@media screen and (max-width:640px)": {
          backgroundColor: everyRemoved?.includes(item?.id) ? "#ffcfcf" : null
        },
        ".editItemMobile": {
          "@media screen and (max-width:640px)": {
            display: "flex",
            gap: "16px",
            alignItems: "center"
          }
        },
        ".editItemMobile::before": {
          "@media screen and (max-width:640px)": {
            content: "attr(data-label)",
            display: "flex",
            minWidth: "64px",
            width: "64px",
            fontWeight: "600",
            textAlign: "left",
            whiteSpace: "nowrap"
          }
        }
      }}
    >
      <div
        data-label={translate("common.name") + ":"}
        className="editItemMobile w-full sm:w-[36%]"
      >
        {item?.service?.name || item?.addon?.name || item?.description}
      </div>
      <div
        data-label={translate("order.type") + ":"}
        className="editItemMobile w-full sm:w-[16%]"
      >
        {item?.type}
      </div>
      {item?.artist ? (
        <div
          data-label={translate("order.artist") + ":"}
          className="editItemMobile w-full sm:w-[36%] px-0 sm:px-3"
        >
          <FormControl
            fullWidth
            size="small"
            sx={{
              backgroundColor: everyEdited?.find((i) => i?.id === item?.id)
                ? "#e3ffdd"
                : null
            }}
          >
            <InputLabel id="artists-custom-select">
              {translate("order.artist")}
            </InputLabel>
            <Select
              labelId="artists-custom-select"
              id="artists-custom-select"
              variant="filled"
              value={selectedArtist}
              disabled={everyRemoved?.includes(item?.id)}
              onChange={(event) => {
                setSelectedArtist(event?.target?.value);
                if (item?.type === "Service") {
                  setEditedArtists((prev) =>
                    prev?.find(
                      (curr) =>
                        curr?.id === item?.id &&
                        item?.artist?.id === event?.target?.value?.id
                    )
                      ? prev?.filter((i) => i.id !== item?.id)
                      : [
                          ...prev,
                          {
                            id: item?.id,
                            artistId: event?.target?.value?.id
                          }
                        ]
                  );
                } else {
                  setEditedAddOnArtists((prev) =>
                    prev?.find(
                      (curr) =>
                        curr?.id === item?.id &&
                        item?.artist?.id === event?.target?.value?.id
                    )
                      ? prev?.filter((i) => i.id !== item?.id)
                      : [
                          ...prev,
                          {
                            id: item?.id,
                            artistId: event?.target?.value?.id
                          }
                        ]
                  );
                }
              }}
              renderValue={(value) => {
                return <span>{value?.name}</span>;
              }}
            >
              {artistChoices
                ?.filter((curr: any) => curr.id !== selectedArtist?.id)
                ?.map((item: any) => (
                  <MenuItem value={item}>{item?.name}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      ) : (
        <div className="hidden sm:block sm:w-[36%] px-3">-</div>
      )}
      <div
        data-label={translate("order.price") + ":"}
        className="editItemMobile w-full mb-3 sm:mb-0 sm:w-[12%]"
      >
        {item?.type === "Discount"
          ? `-$${Math.abs(item?.price)}`
          : `$${item?.price}`}
      </div>
      {everyRemoved?.includes(item?.id) ? (
        <CustomButton
          text={translate("common.undo")}
          onClick={() => {
            if (item?.type === "Service") {
              setRemovedServices((prev) =>
                prev.filter((curr) => curr !== item?.id)
              );
            } else if (item?.type === "Addon") {
              setRemovedAddons((prev) =>
                prev.filter((curr) => curr !== item?.id)
              );
            } else {
              setRemovedItems((prev) =>
                prev.filter((curr) => curr !== item?.id)
              );
            }
          }}
          variant="secondary"
          className="!w-full sm:!w-[78px] !px-2 hover:!bg-mono-white"
        />
      ) : (
        <CustomButton
          text={translate("common.remove")}
          onClick={() => {
            if (item?.type === "Service") {
              setRemovedServices((prev) => [...prev, item?.id]);
              const editedService = editedArtists?.find(
                (curr) => curr?.id === item?.id
              );
              if (editedService) {
                setEditedArtists((prev) =>
                  prev?.filter((i) => i.id !== item?.id)
                );
                setSelectedArtist({
                  id: item?.artist?.id,
                  name: `${item?.artist?.user?.first_name} ${item?.artist?.user?.last_name}`
                });
              }
            } else if (item?.type === "Addon") {
              setRemovedAddons((prev) => [...prev, item?.id]);
              const editedAddon = editedAddOnArtists?.find(
                (curr) => curr?.id === item?.id
              );
              if (editedAddon) {
                setEditedAddOnArtists((prev) =>
                  prev?.filter((i) => i.id !== item?.id)
                );
                setSelectedArtist({
                  id: item?.artist?.id,
                  name: `${item?.artist?.user?.first_name} ${item?.artist?.user?.last_name}`
                });
              }
            } else {
              setRemovedItems((prev) => [...prev, item?.id]);
            }
          }}
          variant="secondary"
          className="!w-full sm:!w-[78px] !px-2"
          sx={{
            border: `solid 1px ${muiColors.error}`,
            color: muiColors.error,
            svg: {
              fill: muiColors.error,
              fillOpacity: 1,
              path: {
                fill: muiColors.error,
                fillOpacity: 1
              }
            }
          }}
        />
      )}
      {everyRemoved?.includes(item?.id) && (
        <>
          <div className="absolute hidden sm:block sm:w-[calc(100%-108px)] h-[1px] top-[35%] sm:top-[50%] bg-error" />
        </>
      )}
    </Box>
  );
};

export default OrderEditItem;
